<script lang="ts" setup>
import { computed } from 'vue'
import VehicleTypeIcon from '@/components/VehicleTypeIcon.vue'
import {
  currencyFilter,
  expirationDelta,
  pluralize,
} from '@/utils/string'
import { isExpiredV2 } from '@/utils/quote'
import SkeletonBox from '@/components/SkeletonBox.vue'
import QuoteSummary from '@/models/dto/quoteSummary/QuoteSummary'

interface Props {
  row?: QuoteSummary
}

const props = withDefaults(defineProps<Props>(), {
  row: null
})

defineEmits<{
  click: []
}>()

const isExpired = computed(() => {
  if (!props.row.expirationDatetime) {
    return false
  }
  return isExpiredV2(props.row.expirationDatetime)
})

const routeName = computed(() => {
  const { pickupLocation, dropoffLocation } = props.row
  if (!pickupLocation || !dropoffLocation) {
    return ''
  }

  return `${pickupLocation} > ${dropoffLocation}`
})

const price = computed(() => {
  const isQuoteExpired = isExpiredV2(props.row.expirationDatetime)
  if (isQuoteExpired) {
    return 'Expired'
  }

  const hasSpecialScenario = props.row.promptCall || props.row.salesWillCall || props.row.promptBASCall
  if (hasSpecialScenario) {
    return 'More Info'
  }

  if (!props.row.price) {
    return 'View Prices'
  }

  return currencyFilter(props.row.price)
})

const expiration = computed(() => {
  const timestamp = props.row.expirationDatetime
  if (!timestamp) {
    return 'Invalid Date'
  }
  if (isExpired.value) {
    return 'Expired'
  }

  return expirationDelta(timestamp)
})

const vehiclesDisplayText = computed(() => {
  return props.row.vehicles.map(({ quantity, label }) => `${quantity} ${pluralize(quantity, label)}`)
})

const displayVehicleType = computed(() => {
  if (isExpired.value) {
    return 'Charter Bus'
  }
  return props.row.vehicles[0]?.label || null
})
</script>

<template>
  <v-card class="padding-a-4 margin-b-2" @click="$emit('click')">
    <div class="d-flex flex-row justify-space-between">
      <div class="d-flex flex-column">
        <div class="margin-r-3 font-bold">{{ routeName }}</div>
        <div>
          <p
            v-for="(vehicle, vehicleIndex) in vehiclesDisplayText"
            :key="`vehicle-${vehicleIndex}-${row.id}`"
            class="padding-y-0 margin-y-0"
          >
            {{ vehicle }}
          </p>
        </div>
      </div>
      <div class="d-flex flex-column text-right">
        <div class="d-flex" v-if="!isExpired">
          <CUIcon
            view-box="0 0 24 24"
            color="gray-light"
            class="margin-r-1"
          >
            person
          </CUIcon>
          <p class="padding-y-0 margin-y-0">
            {{ row.passengerCount }}
          </p>
        </div>
      </div>
    </div>
    <div class="d-flex justify-space-between align-end">
      <div class="margin-r-3">
        <VehicleTypeIcon
          v-if="displayVehicleType"
          :vehicle-type-label="displayVehicleType"
          width="100"
          height="60"
        />
        <SkeletonBox v-else class="w-100 h-60" />
        <br />
        ID
        <span class="font-medium">{{ row.id }}</span>
      </div>
      <div class="text-right">
        <p
          class="margin-y-0 font-bold font-18"
          :class="{ 'text-error': isExpired }"
        >
          {{ price }}
        </p>
        <p
          class="font-14 text-gray-light margin-y-0"
          :class="{ 'opacity-0': isExpired }"
        >
          {{ expiration }}
        </p>
      </div>
    </div>
  </v-card>
</template>
